import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card, Tabs, Input, Button, Typography, Space, Tag, Alert, Spin, Empty, List,
  Row, Col, Select, Table, message as antMessage, Descriptions, DatePicker
} from 'antd';
import {
  RobotOutlined, SendOutlined, BulbOutlined, YoutubeOutlined, MailOutlined,
  TeamOutlined, FileTextOutlined, CopyOutlined, ReloadOutlined, HistoryOutlined,
  QuestionCircleOutlined, AudioOutlined, ThunderboltOutlined, FileExcelOutlined,
  CheckCircleOutlined, UploadOutlined, PictureOutlined, AuditOutlined,
  RiseOutlined, AlertOutlined, ProfileOutlined,
  SafetyOutlined, FallOutlined, CalendarOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../utils/api';
import AiPlanCard, { downloadAiFile } from '../components/AiPlanCard';
import useVoiceInput from '../utils/voice';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

const SUGGESTED = [
  'How much profit this month?',
  'Which client payment is pending?',
  'Which songs are not distributed yet?',
  'Who is absent today?',
  'Which channel had no upload in last 7 days?',
  'Top expense categories this month',
  'This month salary paid to each staff',
];

const copy = (txt) => {
  navigator.clipboard.writeText(txt || '');
  antMessage.success('Copy ho gaya');
};

// Renders the AI's plain text answer with light formatting (headings / bullets).
function AiText({ text }) {
  if (!text) return null;
  return (
    <div style={{ whiteSpace: 'pre-wrap', fontSize: 15, lineHeight: 1.7 }}>
      {text.replace(/\*\*/g, '')}
    </div>
  );
}

// Chhota mic button — jis box ke saath lagao, bol ke likhwane ke liye.
function VoiceBtn({ onText, block, label = 'Bol ke likho' }) {
  const voice = useVoiceInput((t) => onText(t));
  if (!voice.supported) return null;
  return (
    <Button
      block={block}
      danger={voice.listening}
      icon={<AudioOutlined />}
      onClick={voice.toggle}
      title={label}
    >
      {voice.listening ? 'Sun raha hoon...' : label}
    </Button>
  );
}

// Bola hua text purane text ke aage jodta hai.
const appendText = (setter, key) => (t) =>
  setter(prev => (typeof prev === 'object' && prev !== null && key
    ? { ...prev, [key]: (prev[key] ? prev[key] + ' ' : '') + t }
    : (prev ? prev + ' ' : '') + t));

const STAFF_SUGGESTED = [
  'My attendance this month',
  'My pending tasks',
  'Where do I upload thumbnails?',
  'How to paste claim links?',
];

function Assistant({ suggestions = SUGGESTED }) {
  const [thread, setThread] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const voice = useVoiceInput(setQ);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread, loading]);

  const ask = async (question) => {
    const text = (question || q).trim();
    if (!text || loading) return;
    const history = thread.map(m => ({ role: m.me ? 'user' : 'ai', text: m.text }));
    setQ('');
    setThread(t => [...t, { me: true, text }]);
    setLoading(true);
    try {
      const res = await api.post('/ai/ask', { question: text, history });
      if (res.data?.file) downloadAiFile(res.data.file);
      setThread(t => [...t, {
        me: false, text: res.data?.answer || '',
        rows: res.data?.rows, links: res.data?.links, plan: res.data?.plan, q: text,
      }]);
    } catch (err) {
      setThread(t => [...t, { me: false, text: err.response?.data?.error || 'AI se jawab nahi mila' }]);
    }
    setLoading(false);
  };

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        {suggestions.map(s => (
          <Tag key={s} color="purple" style={{ cursor: 'pointer', padding: '4px 10px', fontSize: 13 }} onClick={() => ask(s)}>
            {s}
          </Tag>
        ))}
      </Space>

      <div style={{
        minHeight: 320, maxHeight: 460, overflowY: 'auto', padding: 12,
        background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 10, marginBottom: 12
      }}>
        {thread.length === 0 && !loading && (
          <Empty description="Kuch bhi poochho — apne dashboard ke data se jawab milega" />
        )}
        {thread.map((m, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: m.me ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
            <div style={{
              maxWidth: m.me ? '82%' : '96%', padding: '10px 14px', borderRadius: 12,
              background: m.me ? 'linear-gradient(135deg,#667eea,#4f46e5)' : '#fff',
              color: m.me ? '#fff' : '#111',
              border: m.me ? 'none' : '1px solid #eee'
            }}>
              <AiText text={m.text} />
              {!m.me && m.plan && <AiPlanCard plan={m.plan} />}
              {!m.me && <AnswerExtras rows={m.rows} links={m.links} title={m.q} />}
            </div>
          </div>
        ))}
        {loading && <div style={{ padding: 8 }}><Spin /> <Text type="secondary">AI soch raha hai...</Text></div>}
        <div ref={bottomRef} />
      </div>

      <Space.Compact style={{ width: '100%' }}>
        <Input
          size="large"
          placeholder="Sawal likho — jaise: July me kitna kharcha hua?"
          value={q}
          onChange={e => setQ(e.target.value)}
          onPressEnter={() => ask()}
          disabled={loading}
        />
        {voice.supported && (
          <Button
            size="large"
            danger={voice.listening}
            icon={<AudioOutlined />}
            onClick={voice.toggle}
            title="Bol ke poochho"
          />
        )}
        <Button size="large" type="primary" icon={<SendOutlined />} onClick={() => ask()} loading={loading}>
          Poochho
        </Button>
      </Space.Compact>
    </div>
  );
}

function SeoWriter() {
  const [form, setForm] = useState({ title: '', singer: '', language: 'Hindi', notes: '' });
  const [out, setOut] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!form.title.trim()) { antMessage.warning('Song ka naam likhiye'); return; }
    setLoading(true); setOut(null);
    try {
      const res = await api.post('/ai/seo', form);
      setOut(res.data);
    } catch (err) {
      antMessage.error(err.response?.data?.error || 'AI se jawab nahi mila');
    }
    setLoading(false);
  };

  return (
    <Row gutter={16}>
      <Col xs={24} md={9}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input size="large" placeholder="Song ka naam *" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
          <Input placeholder="Singer" value={form.singer} onChange={e => setForm({ ...form, singer: e.target.value })} />
          <Select
            style={{ width: '100%' }}
            value={form.language}
            onChange={v => setForm({ ...form, language: v })}
            options={['Hindi', 'Rajasthani', 'Haryanvi', 'Punjabi', 'Bhojpuri', 'English'].map(l => ({ value: l, label: l }))}
          />
          <TextArea rows={3} placeholder="Extra detail (lyricist, music, mood, movie...)" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
          <VoiceBtn block onText={appendText(setForm, 'notes')} label="Bol ke detail likho" />
          <Button type="primary" size="large" block icon={<YoutubeOutlined />} onClick={run} loading={loading}>
            SEO banao
          </Button>
        </Space>
      </Col>
      <Col xs={24} md={15}>
        {loading && <Spin />}
        {out && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Card size="small" title="Titles" extra={<Button size="small" icon={<CopyOutlined />} onClick={() => copy((out.titles || []).join('\n'))}>Copy</Button>}>
              <List size="small" dataSource={out.titles || []} renderItem={(t) => <List.Item>{t}</List.Item>} />
            </Card>
            <Card size="small" title="Description" extra={<Button size="small" icon={<CopyOutlined />} onClick={() => copy(out.description)}>Copy</Button>}>
              <div style={{ whiteSpace: 'pre-wrap' }}>{out.description}</div>
            </Card>
            <Card size="small" title="Tags" extra={<Button size="small" icon={<CopyOutlined />} onClick={() => copy((out.tags || []).join(', '))}>Copy</Button>}>
              <Space wrap>{(out.tags || []).map((t, i) => <Tag key={i}>{t}</Tag>)}</Space>
            </Card>
            <Card size="small" title="Hashtags" extra={<Button size="small" icon={<CopyOutlined />} onClick={() => copy((out.hashtags || []).join(' '))}>Copy</Button>}>
              <Space wrap>{(out.hashtags || []).map((t, i) => <Tag color="blue" key={i}>{t}</Tag>)}</Space>
            </Card>
            {!!(out.thumbnail_text || []).length && (
              <Card size="small" title="Thumbnail par likhne ke liye">
                <Space wrap>{out.thumbnail_text.map((t, i) => <Tag color="magenta" key={i}>{t}</Tag>)}</Space>
              </Card>
            )}
          </Space>
        )}
      </Col>
    </Row>
  );
}

function EmailWriter({ isAdmin }) {
  const [form, setForm] = useState({ to_name: '', purpose: '', tone: 'professional', language: 'hinglish', send_to: '' });
  const [out, setOut] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (send) => {
    if (!form.purpose.trim()) { antMessage.warning('Kya likhna hai wo bataiye'); return; }
    if (send && !form.send_to.trim()) { antMessage.warning('Email address daaliye'); return; }
    setLoading(true);
    try {
      const res = await api.post('/ai/email', { ...form, send: !!send });
      setOut(res.data);
      if (send) {
        if (res.data?.sent) antMessage.success('Mail bhej diya');
        else antMessage.error(res.data?.send_error || 'Mail nahi gaya');
      }
    } catch (err) {
      antMessage.error(err.response?.data?.error || 'AI se jawab nahi mila');
    }
    setLoading(false);
  };

  return (
    <Row gutter={16}>
      <Col xs={24} md={9}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input placeholder="Kisko (naam)" value={form.to_name} onChange={e => setForm({ ...form, to_name: e.target.value })} />
          <TextArea rows={4} placeholder="Kis liye mail? jaise: 3 mahine ka payment pending hai, polite reminder bhejo" value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} />
          <VoiceBtn block onText={appendText(setForm, 'purpose')} label="Bol ke bolo kya mail bhejni hai" />
          <Select style={{ width: '100%' }} value={form.tone} onChange={v => setForm({ ...form, tone: v })}
            options={[
              { value: 'professional', label: 'Professional' },
              { value: 'friendly', label: 'Friendly' },
              { value: 'firm but polite', label: 'Sakht par polite' },
            ]} />
          <Select style={{ width: '100%' }} value={form.language} onChange={v => setForm({ ...form, language: v })}
            options={[
              { value: 'hinglish', label: 'Hinglish' },
              { value: 'english', label: 'English' },
              { value: 'hindi', label: 'Hindi' },
            ]} />
          <Button type="primary" size="large" block icon={<MailOutlined />} onClick={() => run(false)} loading={loading}>
            Mail draft karo
          </Button>
          {isAdmin && (
            <>
              <Input placeholder="Bhejna hai? email address" value={form.send_to} onChange={e => setForm({ ...form, send_to: e.target.value })} />
              <Button block onClick={() => run(true)} loading={loading}>Draft bana ke bhej do</Button>
            </>
          )}
        </Space>
      </Col>
      <Col xs={24} md={15}>
        {loading && <Spin />}
        {out && (
          <Card
            size="small"
            title={<b>Subject: {out.subject}</b>}
            extra={<Button size="small" icon={<CopyOutlined />} onClick={() => copy(`${out.subject}\n\n${out.body}`)}>Copy</Button>}
          >
            <div style={{ whiteSpace: 'pre-wrap' }}>{out.body}</div>
          </Card>
        )}
      </Col>
    </Row>
  );
}

function Insights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/ai/insights');
      setData(res.data);
    } catch (err) {
      antMessage.error(err.response?.data?.error || 'AI se jawab nahi mila');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const snap = data?.data?.snapshot || {};
  const paisa = snap.paisa || {};

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Dobara banao</Button>
      </Space>
      {loading && !data && <Spin />}
      {data && (
        <>
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 4 }} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Is mahine Income">₹{paisa.is_mahine_income ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="Is mahine Expense">₹{paisa.is_mahine_expense ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="Profit">₹{paisa.is_mahine_profit ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="Client Unpaid">${paisa.client_unpaid_usd ?? '-'}</Descriptions.Item>
          </Descriptions>
          <Card><AiText text={data.insights} /></Card>
        </>
      )}
    </div>
  );
}

function StaffReview() {
  const [month, setMonth] = useState(dayjs());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (m) => {
    setLoading(true);
    try {
      const res = await api.get('/ai/staff-review', { params: { month: (m || month).format('YYYY-MM') } });
      setData(res.data);
    } catch (err) {
      antMessage.error(err.response?.data?.error || 'AI se jawab nahi mila');
    }
    setLoading(false);
  };

  useEffect(() => {
    load(dayjs());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <DatePicker picker="month" value={month} onChange={(v) => { if (v) { setMonth(v); load(v); } }} allowClear={false} />
        <Button icon={<ReloadOutlined />} onClick={() => load()} loading={loading}>Review banao</Button>
      </Space>
      {loading && !data && <Spin />}
      {data && (
        <>
          <Table
            size="small"
            style={{ marginBottom: 16 }}
            rowKey="id"
            dataSource={data.staff || []}
            scroll={{ x: true }}
            pagination={false}
            columns={[
              { title: 'Staff', dataIndex: 'full_name', fixed: 'left' },
              { title: 'Role', dataIndex: 'role' },
              { title: 'Present', dataIndex: 'present_days' },
              { title: 'Absent', dataIndex: 'absent_days' },
              { title: 'Task done', dataIndex: 'tasks_done' },
              { title: 'Task pending', dataIndex: 'tasks_pending' },
              { title: 'Work entries', dataIndex: 'work_entries' },
              { title: 'Thumbnails', dataIndex: 'thumbnails' },
              { title: 'Claim links', dataIndex: 'claim_links' },
              { title: 'Videos', dataIndex: 'videos_uploaded' },
            ]}
          />
          <Card><AiText text={data.review} /></Card>
        </>
      )}
    </div>
  );
}

function DailyReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async (send) => {
    send ? setSending(true) : setLoading(true);
    try {
      const res = await api.get('/ai/daily-report', { params: send ? { send: 1 } : {} });
      setData(res.data);
      if (send) {
        if (res.data?.emailed) antMessage.success('Report mail kar di');
        else antMessage.warning('Mail nahi gaya (mail settings check karo)');
      }
    } catch (err) {
      antMessage.error(err.response?.data?.error || 'AI se jawab nahi mila');
    }
    send ? setSending(false) : setLoading(false);
  }, []);

  useEffect(() => { load(false); }, [load]);

  return (
    <div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Ye report roz raat 9 baje apne aap aapke mail par bhi chali jaati hai."
      />
      <Space style={{ marginBottom: 12 }} wrap>
        <Button icon={<ReloadOutlined />} onClick={() => load(false)} loading={loading}>Aaj ki report banao</Button>
        <Button type="primary" icon={<MailOutlined />} onClick={() => load(true)} loading={sending}>Mail par bhejo</Button>
      </Space>
      {loading && !data && <Spin />}
      {data && <Card title={`AI Daily Report — ${dayjs(data.date).format('DD MMM YYYY')}`}><AiText text={data.report} /></Card>}
    </div>
  );
}

function Guide() {
  const [sections, setSections] = useState({});
  const [q, setQ] = useState('');
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/ai/guide').then(res => setSections(res.data?.sections || {})).catch(() => {});
  }, []);

  const ask = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const res = await api.get('/ai/guide', { params: { q } });
      setAnswer(res.data?.answer || '');
    } catch (err) {
      antMessage.error(err.response?.data?.error || 'AI se jawab nahi mila');
    }
    setLoading(false);
  };

  return (
    <div>
      <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
        <Input
          size="large"
          placeholder="Kaise karna hai? jaise: client ka statement kahan se download hoga?"
          value={q} onChange={e => setQ(e.target.value)} onPressEnter={ask}
        />
        <Button size="large" type="primary" icon={<QuestionCircleOutlined />} onClick={ask} loading={loading}>Poochho</Button>
      </Space.Compact>
      {answer && <Card style={{ marginBottom: 16 }}><AiText text={answer} /></Card>}
      <List
        bordered
        dataSource={Object.entries(sections)}
        renderItem={([name, desc]) => (
          <List.Item>
            <div>
              <b>{name}</b>
              <div><Text type="secondary">{desc}</Text></div>
            </div>
          </List.Item>
        )}
      />
    </div>
  );
}

function AiLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/ai/log').then(res => setRows(Array.isArray(res.data) ? res.data : [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <Table
      size="small"
      loading={loading}
      rowKey="id"
      dataSource={rows}
      expandable={{ expandedRowRender: (r) => <div style={{ whiteSpace: 'pre-wrap' }}>{r.answer}</div> }}
      columns={[
        { title: 'Kab', dataIndex: 'created_at', width: 160, render: (v) => dayjs(v).format('DD MMM, hh:mm A') },
        { title: 'Kisne', dataIndex: 'full_name', width: 160 },
        { title: 'Kaam', dataIndex: 'kind', width: 120, render: (v) => <Tag color="purple">{v}</Tag> },
        { title: 'Sawal', dataIndex: 'question', ellipsis: true },
      ]}
    />
  );
}

const ACTION_LABEL = {
  add_expense: 'Expense add karna',
  add_income: 'Income add karna',
  assign_task: 'Staff ko task dena',
  send_email: 'Email bhejna',
};

const ACTION_EXAMPLES = [
  'Aaj studio ka 5000 kharch add karo',
  'Ramesh ko kal ka task do — 5 thumbnail banao',
  'Aaj 12000 ki income add karo, category youtube',
];

// AI Actions — you say what to do, AI turns it into a dashboard action, you confirm,
// then it actually runs. Nothing is written before the Confirm button.
function AiActions() {
  const [cmd, setCmd] = useState('');
  const [plan, setPlan] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(null);
  const voice = useVoiceInput(setCmd);

  const makePlan = async (text) => {
    const c = (text || cmd).trim();
    if (!c || planning) return;
    setPlan(null); setDone(null); setPlanning(true);
    try {
      const res = await api.post('/ai/plan', { command: c });
      if (res.data?.action === 'none') antMessage.warning('AI samajh nahi paaya — thoda saaf likho');
      setPlan(res.data);
    } catch (err) {
      antMessage.error(err.response?.data?.error || 'Plan nahi ban paaya');
    }
    setPlanning(false);
  };

  const run = async () => {
    if (!plan || running) return;
    setRunning(true);
    try {
      const res = await api.post('/ai/run', { action: plan.action, params: plan.params });
      setDone(res.data?.message || 'Ho gaya');
      setPlan(null);
      setCmd('');
    } catch (err) {
      antMessage.error(err.response?.data?.error || 'Kaam nahi ho paaya');
    }
    setRunning(false);
  };

  const setParam = (k, v) => setPlan(p => ({ ...p, params: { ...p.params, [k]: v } }));

  return (
    <div>
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="Bolo ya likho — AI dashboard me kaam kar dega"
        description="Pehle AI plan dikhayega, aap check karke Confirm karoge, tabhi entry hogi."
      />
      <Space wrap style={{ marginBottom: 12 }}>
        {ACTION_EXAMPLES.map(s => (
          <Tag key={s} color="volcano" style={{ cursor: 'pointer', padding: '4px 10px', fontSize: 13 }} onClick={() => { setCmd(s); makePlan(s); }}>
            {s}
          </Tag>
        ))}
      </Space>

      <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
        <Input
          size="large"
          placeholder="Jaise: aaj studio ka 5000 kharch add karo"
          value={cmd}
          onChange={e => setCmd(e.target.value)}
          onPressEnter={() => makePlan()}
        />
        {voice.supported && (
          <Button size="large" danger={voice.listening} icon={<AudioOutlined />} onClick={voice.toggle} title="Bol ke bolo" />
        )}
        <Button size="large" type="primary" icon={<ThunderboltOutlined />} onClick={() => makePlan()} loading={planning}>
          Plan banao
        </Button>
      </Space.Compact>

      {done && <Alert type="success" showIcon icon={<CheckCircleOutlined />} message={done} style={{ marginBottom: 12 }} />}

      {plan && plan.action !== 'none' && (
        <Card size="small" title={ACTION_LABEL[plan.action] || plan.action} style={{ marginBottom: 12 }}>
          {plan.summary && <Paragraph style={{ marginBottom: 12 }}>{plan.summary}</Paragraph>}
          <Row gutter={[10, 10]}>
            {Object.entries(plan.params || {}).filter(([k]) => k !== 'assigned_to').map(([k, v]) => (
              <Col xs={24} sm={12} key={k}>
                <Text type="secondary" style={{ fontSize: 12 }}>{k}</Text>
                <Input value={v ?? ''} onChange={e => setParam(k, e.target.value)} />
              </Col>
            ))}
          </Row>
          {plan.missing?.length > 0 && (
            <Alert
              type="warning" showIcon style={{ marginTop: 12 }}
              message={'Ye cheez missing hai: ' + plan.missing.join(', ')}
            />
          )}
          <Space style={{ marginTop: 14 }}>
            <Button type="primary" icon={<CheckCircleOutlined />} onClick={run} loading={running}>
              Confirm karke kar do
            </Button>
            <Button onClick={() => setPlan(null)}>Cancel</Button>
          </Space>
        </Card>
      )}
    </div>
  );
}

// AI Excel Reader — AI reads any sheet's headers and says which dashboard field each
// column is. Saving teaches the normal Excel import this wording permanently.
function ExcelReader() {
  const [rows, setRows] = useState(null);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [savedCount, setSavedCount] = useState(null);
  const fileRef = useRef(null);
  const lastFile = useRef(null);

  const send = async (file, save) => {
    setLoading(true); setSavedCount(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (save) fd.append('save', '1');
      const res = await api.post('/ai/excel-map', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setRows(res.data?.mapping || []);
      setNote(res.data?.note || '');
      if (save) setSavedCount(res.data?.saved ?? 0);
    } catch (err) {
      antMessage.error(err.response?.data?.error || 'File padhi nahi ja saki');
    }
    setLoading(false);
  };

  return (
    <div>
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="Koi bhi Excel/CSV daalo — AI khud samjhega kaunsa column kya hai"
        description="Save karne par aage se normal Excel upload bhi isi wording ko pehchan lega."
      />
      <input
        ref={fileRef}
        type="file"
        accept=".csv,.xls,.xlsx"
        style={{ display: 'none' }}
        onChange={e => {
          const f = e.target.files?.[0];
          if (f) { lastFile.current = f; send(f, false); }
          e.target.value = '';
        }}
      />
      <Space wrap style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<UploadOutlined />} onClick={() => fileRef.current?.click()} loading={loading}>
          Excel/CSV choose karo
        </Button>
        {rows?.length > 0 && (
          <Button icon={<CheckCircleOutlined />} onClick={() => lastFile.current && send(lastFile.current, true)} loading={loading}>
            Ye mapping yaad rakho
          </Button>
        )}
      </Space>

      {savedCount !== null && (
        <Alert type="success" showIcon style={{ marginBottom: 12 }} message={`${savedCount} column yaad rakh liye`} />
      )}
      {note && <Paragraph type="secondary">{note}</Paragraph>}
      {rows && (
        <Table
          size="small"
          rowKey={(r) => r.column}
          dataSource={rows}
          pagination={false}
          columns={[
            { title: 'Excel ka column', dataIndex: 'column' },
            {
              title: 'Dashboard field', dataIndex: 'field', width: 200,
              render: (v) => v === 'ignore' ? <Tag>kaam ka nahi</Tag> : <Tag color="green">{v}</Tag>
            },
            { title: 'Kyun', dataIndex: 'why', ellipsis: true },
          ]}
        />
      )}
    </div>
  );
}

const inr = (v) => '₹' + Number(v || 0).toLocaleString('en-IN');
const usd = (v) => '$' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

// AI Profit & Loss — month ke numbers + AI ki salah (kahan paisa ja raha hai).
function PnlAdvice() {
  const [month, setMonth] = useState(dayjs());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (m) => {
    setLoading(true);
    try {
      const res = await api.get('/ai/pnl', { params: { month: (m || month).format('YYYY-MM') } });
      setData(res.data);
    } catch (err) {
      antMessage.error(err.response?.data?.error || 'P&L nahi bana');
    }
    setLoading(false);
  };

  useEffect(() => {
    load(dayjs());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const n = data?.numbers || {};
  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <DatePicker picker="month" value={month} onChange={v => { if (v) { setMonth(v); load(v); } }} allowClear={false} />
        <Button icon={<ReloadOutlined />} onClick={() => load()} loading={loading}>Salah lo</Button>
      </Space>

      {loading && !data && <div><Spin /> <Text type="secondary">Hisab dekh raha hoon...</Text></div>}

      {data && (
        <>
          <Row gutter={[10, 10]} style={{ marginBottom: 12 }}>
            {[
              ['Income (manual)', inr(n.income_manual_inr)],
              ['Kul kharch', inr(n.kul_kharch_inr)],
              ['Net (₹)', inr(n.net_inr)],
              ['Client channel revenue', usd(n.client_channel_revenue_usd)],
              ['Client se pending', usd(n.unpaid_client_revenue_usd)],
              ['Pichhla mahina kharch', inr(n.pichhla_mahina_kharch)],
            ].map(([label, val]) => (
              <Col xs={12} sm={8} md={4} key={label}>
                <Card size="small">
                  <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
                  <div style={{ fontSize: 17, fontWeight: 600 }}>{val}</div>
                </Card>
              </Col>
            ))}
          </Row>
          <Card size="small" style={{ marginBottom: 12 }} extra={<Button size="small" icon={<CopyOutlined />} onClick={() => copy(data.advice)}>Copy</Button>}>
            <AiText text={data.advice} />
          </Card>
          <Table
            size="small" rowKey="category" pagination={false}
            dataSource={(n.expense_category_wise || []).map(r => ({ ...r, key: r.category }))}
            columns={[
              { title: 'Kharch category', dataIndex: 'category' },
              { title: 'Total', dataIndex: 'total', align: 'right', render: v => inr(v) },
              { title: 'Entries', dataIndex: 'entries', align: 'right', width: 90 },
            ]}
          />
        </>
      )}
    </div>
  );
}

// Client payment follow-up — pending clients + AI ka ready reminder. Bhejna admin
// ke haath me: email address daalo, phir Send (koi auto-send nahi).
function ClientFollowup() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emails, setEmails] = useState({});
  const [sending, setSending] = useState('');

  const load = async () => {
    setLoading(true);
    try { setData((await api.get('/ai/client-followup')).data); }
    catch (err) { antMessage.error(err.response?.data?.error || 'Follow-up list nahi mili'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const send = async (d) => {
    const to = (emails[d.client_name] || '').trim();
    if (!to) return antMessage.warning('Pehle client ka email daalo');
    setSending(d.client_name);
    try {
      const res = await api.post('/ai/email', {
        to_email: to, to_name: d.client_name, purpose: d.message, send: true, subject: d.subject, body: d.message
      });
      antMessage[res.data?.sent ? 'success' : 'warning'](res.data?.sent ? 'Mail bhej diya' : (res.data?.send_error || 'Mail nahi gaya'));
    } catch (err) {
      antMessage.error(err.response?.data?.error || 'Mail nahi gaya');
    }
    setSending('');
  };

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Dobara dekho</Button>
        {data?.note && <Text type="secondary">{data.note}</Text>}
      </Space>

      {loading && !data && <div><Spin /> <Text type="secondary">Pending payment dekh raha hoon...</Text></div>}

      {data && (data.pending?.length === 0
        ? <Empty description="Kisi client ka payment pending nahi hai" />
        : (
          <>
            <Table
              size="small" rowKey="client_name" pagination={false} style={{ marginBottom: 12 }}
              dataSource={(data.pending || []).map(r => ({ ...r, key: r.client_name }))}
              columns={[
                { title: 'Client', dataIndex: 'client_name' },
                { title: 'Channels', dataIndex: 'channels', width: 90, align: 'right' },
                { title: 'Pending', dataIndex: 'pending_usd', align: 'right', render: v => usd(v) },
                { title: 'Months', dataIndex: 'months', ellipsis: true },
              ]}
            />
            <AnswerExtras
              showTable={false}
              title="Pending Client Payments"
              rows={data.pending || []}
              links={[{ label: 'Client Channels', path: '/client-payments' }]}
            />
            {(data.drafts || []).map(d => (
              <Card key={d.client_name} size="small" style={{ marginBottom: 10 }}
                title={<Space><TeamOutlined />{d.client_name}</Space>}
                extra={<Button size="small" icon={<CopyOutlined />} onClick={() => copy(`${d.subject}\n\n${d.message}`)}>Copy</Button>}>
                <Text strong>{d.subject}</Text>
                <Paragraph style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}>{d.message}</Paragraph>
                <Space.Compact style={{ width: '100%', maxWidth: 460 }}>
                  <Input placeholder="client ka email" value={emails[d.client_name] || ''}
                    onChange={e => setEmails(s => ({ ...s, [d.client_name]: e.target.value }))} />
                  <Button type="primary" icon={<MailOutlined />} loading={sending === d.client_name} onClick={() => send(d)}>
                    Bhej do
                  </Button>
                </Space.Compact>
              </Card>
            ))}
          </>
        ))}
    </div>
  );
}

// Client statement AI summary — 5-6 line jo seedha client ko bheja ja sake.
function ClientSummary() {
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/ai/client-summary').then(r => setClients(r.data?.clients || [])).catch(() => {});
  }, []);

  const load = async (c) => {
    if (!c) return;
    setLoading(true); setData(null);
    try { setData((await api.get('/ai/client-summary', { params: { client: c } })).data); }
    catch (err) { antMessage.error(err.response?.data?.error || 'Summary nahi bani'); }
    setLoading(false);
  };

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          style={{ minWidth: 240 }} placeholder="Client chuno" showSearch value={client || undefined}
          options={clients.map(c => ({ value: c, label: c }))}
          onChange={v => { setClient(v); load(v); }}
        />
        <Button icon={<ReloadOutlined />} disabled={!client} onClick={() => load(client)} loading={loading}>Summary banao</Button>
      </Space>

      {loading && <div><Spin /> <Text type="secondary">Client ka hisab dekh raha hoon...</Text></div>}

      {data && (
        <>
          <Card size="small" style={{ marginBottom: 12 }} extra={<Button size="small" icon={<CopyOutlined />} onClick={() => copy(data.summary)}>Copy</Button>}>
            <AiText text={data.summary} />
          </Card>
          <Table
            size="small" rowKey="month" pagination={false}
            dataSource={(data.months || []).map(r => ({ ...r, key: r.month || 'blank' }))}
            columns={[
              { title: 'Month', dataIndex: 'month', render: v => v || '(blank)' },
              { title: 'Revenue', dataIndex: 'revenue_usd', align: 'right', render: v => usd(v) },
              { title: 'Advance', dataIndex: 'advance_inr', align: 'right', render: v => inr(v) },
              { title: 'Channels', dataIndex: 'channels', align: 'right', width: 90 },
              { title: 'Paid channels', dataIndex: 'paid_channels', align: 'right', width: 120 },
            ]}
          />
        </>
      )}
    </div>
  );
}

// Claim / copyright assistant — kaunsi claim ruki hai, kya karna hai.
function ClaimAssistant() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setData((await api.get('/ai/claim-check')).data); }
    catch (err) { antMessage.error(err.response?.data?.error || 'Claim report nahi bani'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const d = data?.data || {};
  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Dobara dekho</Button>
        {(d.ditto_status || []).map(s => <Tag key={s.status} color={s.status === 'pending' ? 'gold' : 'green'}>{s.status}: {s.kitne}</Tag>)}
      </Space>

      {loading && !data && <div><Spin /> <Text type="secondary">Claim data dekh raha hoon...</Text></div>}

      {data && (
        <>
          <Card size="small" style={{ marginBottom: 12 }} extra={<Button size="small" icon={<CopyOutlined />} onClick={() => copy(data.report)}>Copy</Button>}>
            <AiText text={data.report} />
          </Card>
          <Tabs
            size="small"
            items={[
              ['company_wise_15din', 'Company (15 din)'],
              ['staff_wise_15din', 'Staff (15 din)'],
              ['ditto_pending', 'Ditto pending'],
              ['ditto_result_problem', 'Ditto me dikkat'],
            ].map(([k, label]) => {
              const rows = d[k] || [];
              return {
                key: k,
                label: `${label} (${rows.length})`,
                children: rows.length === 0 ? <Empty description="Kuch nahi" /> : (
                  <Table size="small" pagination={{ pageSize: 10 }} rowKey={(r, i) => r.id ?? i}
                    dataSource={rows.map((r, i) => ({ key: i, ...r }))}
                    columns={Object.keys(rows[0]).map(c => ({ title: c, dataIndex: c, ellipsis: true }))}
                    scroll={{ x: true }} />
                )
              };
            })}
          />
        </>
      )}
    </div>
  );
}

// Channel alert — revenue gir raha hai ya upload ruk gaya, dono ek jagah.
function ChannelAlert() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setData((await api.get('/ai/channel-alert')).data); }
    catch (err) { antMessage.error(err.response?.data?.error || 'Channel report nahi bani'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ marginBottom: 12 }}>Dobara dekho</Button>

      {loading && !data && <div><Spin /> <Text type="secondary">Channel dekh raha hoon...</Text></div>}

      {data && (
        <>
          <Card size="small" style={{ marginBottom: 12 }} extra={<Button size="small" icon={<CopyOutlined />} onClick={() => copy(data.report)}>Copy</Button>}>
            <AiText text={data.report} />
          </Card>
          <Title level={5}>Revenue gir rahe channel ({(data.girte_channel || []).length})</Title>
          <Table
            size="small" rowKey="channel_name" pagination={{ pageSize: 10 }} style={{ marginBottom: 16 }}
            dataSource={(data.girte_channel || []).map((r, i) => ({ key: i, ...r }))}
            columns={[
              { title: 'Channel', dataIndex: 'channel_name' },
              { title: 'Client', dataIndex: 'client_name', render: v => v || '—' },
              { title: 'Last month', dataIndex: 'last_month', align: 'right', render: v => usd(v) },
              { title: 'Pehle ka average', dataIndex: 'pehle_ka_average', align: 'right', render: v => usd(v) },
              {
                title: 'Gira', key: 'drop', align: 'right',
                render: (_, r) => {
                  const drop = Number(r.pehle_ka_average || 0) - Number(r.last_month || 0);
                  return <Text style={{ color: '#cf1322' }}>-{usd(drop)}</Text>;
                }
              },
            ]}
          />
          <Title level={5}>Sabse purana upload / kaam</Title>
          <Table
            size="small" rowKey="channel_name" pagination={{ pageSize: 10 }}
            dataSource={(data.purane_upload || []).map((r, i) => ({ key: i, ...r }))}
            columns={[
              { title: 'Channel', dataIndex: 'channel_name' },
              { title: 'Last upload', dataIndex: 'last_upload', render: v => v || 'kabhi nahi' },
              { title: 'Last kaam', dataIndex: 'last_work', render: v => v || 'kabhi nahi' },
            ]}
          />
        </>
      )}
    </div>
  );
}

// Upload plan — agle kuch din kis din kis channel par kya daalna hai.
function UploadPlan() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);

  const load = async (d) => {
    setLoading(true);
    try { setData((await api.get('/ai/upload-plan', { params: { days: d || days } })).data); }
    catch (err) { antMessage.error(err.response?.data?.error || 'Plan nahi bana'); }
    setLoading(false);
  };

  const planText = (data?.plan || [])
    .map(d => `${d.date}\n` + (d.items || []).map(i => `  • ${i.song_title} → ${i.channel_name}`).join('\n'))
    .join('\n\n');

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Select value={days} style={{ width: 130 }} onChange={v => { setDays(v); load(v); }}
          options={[3, 7, 10, 14].map(n => ({ value: n, label: `${n} din ka plan` }))} />
        <Button type="primary" icon={<ReloadOutlined />} onClick={() => load()} loading={loading}>Plan banao</Button>
        {data && <Button icon={<CopyOutlined />} onClick={() => copy(planText)}>Copy</Button>}
        {data?.pending_count ? <Text type="secondary">{data.pending_count} gaane pending hain</Text> : null}
      </Space>

      {loading && <div><Spin /> <Text type="secondary">Plan bana raha hoon...</Text></div>}
      {data?.note && <Alert type="info" showIcon message={data.note} style={{ marginBottom: 12 }} />}

      <Row gutter={[10, 10]}>
        {(data?.plan || []).map(day => (
          <Col xs={24} sm={12} md={8} key={day.date}>
            <Card size="small" title={dayjs(day.date).isValid() ? dayjs(day.date).format('DD MMM (ddd)') : day.date}>
              {(day.items || []).map((i, k) => (
                <div key={k} style={{ marginBottom: 8 }}>
                  <Text strong>{i.song_title}</Text>
                  <div><Tag color="blue">{i.channel_name}</Tag></div>
                  {i.kyun && <Text type="secondary" style={{ fontSize: 12 }}>{i.kyun}</Text>}
                </div>
              ))}
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
}

const VERDICT_COLOR = { ok: 'green', 'thik-hai': 'gold', kharab: 'red' };

// Thumbnail AI check — AI looks at the image and flags blur / unreadable text /
// wrong poster before it goes on YouTube.
function ThumbCheck() {
  const [res, setRes] = useState(null);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const check = async (file) => {
    setLoading(true); setRes(null);
    setPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (title.trim()) fd.append('song_title', title.trim());
      const r = await api.post('/ai/thumb-check', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setRes(r.data);
    } catch (err) {
      antMessage.error(err.response?.data?.error || 'Thumbnail check nahi ho paaya');
    }
    setLoading(false);
  };

  return (
    <div>
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="Thumbnail upload karne se pehle AI se check karwa lo"
        description="AI batayega: chehra saaf hai ya nahi, text padhne layak hai, quality low hai, ya galat gaane ka poster hai."
      />
      <Row gutter={[10, 10]} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={12}>
          <Space.Compact style={{ width: '100%' }}>
            <Input placeholder="Gaane ka naam (optional — galat poster pakadne ke liye)" value={title} onChange={e => setTitle(e.target.value)} />
            <VoiceBtn onText={appendText(setTitle)} label="" />
          </Space.Compact>
        </Col>
        <Col xs={24} sm={12}>
          <input
            ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) check(f); e.target.value = ''; }}
          />
          <Button type="primary" icon={<UploadOutlined />} onClick={() => fileRef.current?.click()} loading={loading}>
            Thumbnail choose karo
          </Button>
        </Col>
      </Row>

      {preview && (
        <img src={preview} alt="thumbnail" style={{ maxWidth: 320, borderRadius: 10, marginBottom: 12, border: '1px solid #eee' }} />
      )}
      {loading && <div><Spin /> <Text type="secondary">AI thumbnail dekh raha hai...</Text></div>}

      {res && (
        <Card size="small">
          <Space wrap style={{ marginBottom: 10 }}>
            <Tag color={VERDICT_COLOR[res.verdict] || 'blue'} style={{ fontSize: 14, padding: '3px 10px' }}>
              {res.verdict || 'result'}
            </Tag>
            {res.score !== null && res.score !== undefined && <Tag color="purple">Score: {res.score}/10</Tag>}
            {res.title_match && (
              <Tag color={res.title_match === 'match' ? 'green' : res.title_match === 'galat' ? 'red' : 'orange'}>
                Naam: {res.title_match}
              </Tag>
            )}
          </Space>
          {res.image_text && <Paragraph type="secondary" style={{ marginBottom: 8 }}>Image par likha: {res.image_text}</Paragraph>}
          {res.issues?.length > 0 && (
            <>
              <Text strong>Dikkat:</Text>
              <ul style={{ marginTop: 4 }}>{res.issues.map((i, k) => <li key={k}>{i}</li>)}</ul>
            </>
          )}
          {res.suggestions?.length > 0 && (
            <>
              <Text strong>Sudhaar:</Text>
              <ul style={{ marginTop: 4 }}>{res.suggestions.map((i, k) => <li key={k}>{i}</li>)}</ul>
            </>
          )}
        </Card>
      )}
    </div>
  );
}

const SONG_CHECK_LABEL = {
  duplicate_titles: 'Do baar pade gaane',
  not_distributed: 'Distribute nahi hue',
  metadata_adhoori: 'Metadata adhoori',
  poster_nahi: 'Poster nahi hai',
};

// Song library health — duplicates / not distributed / missing metadata / missing poster,
// with AI's short "aaj ye 3 kaam karo" summary. Also mailed by cron every morning.
function SongCheck() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (send) => {
    setLoading(true);
    try {
      const res = await api.get('/ai/song-check' + (send ? '?send=1' : ''));
      setData(res.data);
      if (send) antMessage.success(res.data?.sent ? 'Report mail ho gayi' : 'Mail nahi ja paayi');
    } catch (err) {
      antMessage.error(err.response?.data?.error || 'Check nahi ho paaya');
    }
    setLoading(false);
  };

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} onClick={() => load(false)} loading={loading}>Dobara check karo</Button>
        <Button icon={<MailOutlined />} onClick={() => load(true)} loading={loading}>Mail bhejo</Button>
        <Text type="secondary" style={{ fontSize: 12 }}>Roz subah ye report apne aap mail ho jaati hai.</Text>
      </Space>

      {loading && !data && <div><Spin /> <Text type="secondary">Song library check ho rahi hai...</Text></div>}

      {data && (
        <>
          <Row gutter={[10, 10]} style={{ marginBottom: 12 }}>
            {Object.entries(data.counts || {}).map(([k, v]) => (
              <Col xs={12} sm={6} key={k}>
                <Card size="small">
                  <Text type="secondary" style={{ fontSize: 12 }}>{SONG_CHECK_LABEL[k] || k}</Text>
                  <div style={{ fontSize: 22, fontWeight: 600 }}>{v}</div>
                </Card>
              </Col>
            ))}
          </Row>
          <Card size="small" style={{ marginBottom: 12 }} extra={<Button size="small" icon={<CopyOutlined />} onClick={() => copy(data.report)}>Copy</Button>}>
            <AiText text={data.report} />
          </Card>
          <Tabs
            size="small"
            items={Object.entries(data.data || {}).map(([k, rows]) => ({
              key: k,
              label: `${SONG_CHECK_LABEL[k] || k} (${rows.length})`,
              children: rows.length === 0 ? <Empty description="Sab theek hai" /> : (
                <AnswerExtras
                  rows={rows}
                  title={SONG_CHECK_LABEL[k] || k}
                  links={[{ label: 'Song Library', path: '/songs' }, { label: 'Song Metadata', path: '/song-metadata' }]}
                />
              )
            }))}
          />
        </>
      )}
    </div>
  );
}

// Downloads a base64 file (xlsx / pdf) that the API returned.
const downloadB64 = (file) => {
  if (!file?.data) return;
  const bin = atob(file.data);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([buf], { type: file.mime || 'application/octet-stream' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name || 'ai-file';
  a.click();
  URL.revokeObjectURL(url);
};

// Answer ke neeche: poori list, uska Excel, aur "jahan ye data hai wo page kholo" buttons.
function AnswerExtras({ rows, links, title, showTable = true }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const hasRows = Array.isArray(rows) && rows.length > 0;
  const hasLinks = Array.isArray(links) && links.length > 0;
  if (!hasRows && !hasLinks) return null;

  const excel = async () => {
    setBusy(true);
    try {
      const r = await api.post('/ai/rows-xlsx', { rows, title: (title || 'AI List').slice(0, 60) });
      downloadB64(r.data?.file);
    } catch (err) {
      antMessage.error(err.response?.data?.error || 'Excel nahi ban paayi');
    }
    setBusy(false);
  };

  return (
    <div style={{ marginTop: 10 }}>
      {hasRows && showTable && (
        <Table
          size="small"
          dataSource={rows.map((r, idx) => ({ key: idx, ...r }))}
          columns={Object.keys(rows[0]).map(k => ({ title: k, dataIndex: k, ellipsis: true }))}
          pagination={rows.length > 8 ? { pageSize: 8, size: 'small' } : false}
          scroll={{ x: true }}
        />
      )}
      <Space wrap style={{ marginTop: 8 }}>
        {hasRows && (
          <Button size="small" icon={<FileExcelOutlined />} loading={busy} onClick={excel}>
            Download Excel ({rows.length})
          </Button>
        )}
        {hasLinks && links.map(l => (
          <Button key={l.path} size="small" type="link" onClick={() => navigate(l.path)}>
            Open {l.label} →
          </Button>
        ))}
      </Space>
    </div>
  );
}

// "AI se banwao" — Excel sheet / PDF / document, chaahe apne data se ya dashboard ke live data se.
function AiMaker({ isAdmin }) {
  const [prompt, setPrompt] = useState('');
  const [format, setFormat] = useState('excel');
  const [useData, setUseData] = useState(!!isAdmin);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const voice = useVoiceInput((t) => setPrompt(p => (p ? p + ' ' : '') + t));

  const make = async () => {
    if (!prompt.trim()) return antMessage.warning('Kya banana hai wo likho');
    setLoading(true); setRes(null);
    try {
      const r = await api.post('/ai/make', { prompt: prompt.trim(), format, use_data: useData });
      setRes(r.data);
      if (r.data?.file) downloadB64(r.data.file);
    } catch (err) {
      antMessage.error(err.response?.data?.error || 'File nahi ban paayi');
    }
    setLoading(false);
  };

  const cols = res?.columns || [];
  return (
    <div>
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="Jo chahiye bolo — Excel sheet, PDF report ya document AI bana ke de dega"
        description='Jaise: "Nirmal ki June ki salary ki Excel banao", "July ke saare expense ki Excel banao", "client ke liye payment reminder ka PDF letter banao". Dashboard ka data chahiye to "live data se bharo" wala option rakho (admin ke liye ye pehle se chalu hai).'
      />
      <TextArea
        rows={3} value={prompt} onChange={e => setPrompt(e.target.value)}
        placeholder="Kya banana hai? (jaise: is mahine ke expense ki Excel category-wise)"
        style={{ marginBottom: 10 }}
      />
      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          value={format} onChange={setFormat} style={{ width: 170 }}
          options={[
            { value: 'excel', label: 'Excel (.xlsx)' },
            { value: 'pdf', label: 'PDF' },
            { value: 'text', label: 'Sirf dekho (file nahi)' },
          ]}
        />
        {isAdmin && (
          <Select
            value={useData ? 'live' : 'mera'} onChange={v => setUseData(v === 'live')} style={{ width: 240 }}
            options={[
              { value: 'live', label: 'Dashboard ke live data se bharo' },
              { value: 'mera', label: 'Data main bata raha hoon' },
            ]}
          />
        )}
        {voice.supported && (
          <Button icon={<AudioOutlined />} danger={voice.listening} onClick={voice.toggle}>
            {voice.listening ? 'Sun raha hoon...' : 'Bol ke batao'}
          </Button>
        )}
        <Button type="primary" icon={<FileExcelOutlined />} loading={loading} onClick={make}>Banao</Button>
      </Space>

      {loading && <div><Spin /> <Text type="secondary">AI bana raha hai...</Text></div>}

      {res && (
        <Card
          size="small" title={res.title}
          extra={res.file && <Button size="small" type="primary" icon={<FileTextOutlined />} onClick={() => downloadB64(res.file)}>
            Dobara download
          </Button>}
        >
          {res.intro && <Paragraph>{res.intro}</Paragraph>}
          {res.body && <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{res.body}</Paragraph>}
          {res.data_note && <Alert type="warning" showIcon style={{ marginBottom: 10 }} message={res.data_note} />}
          {cols.length > 0 && (
            <Table
              size="small" pagination={{ pageSize: 20, size: 'small' }} scroll={{ x: true }}
              dataSource={(res.rows || []).map((r, i) => ({ key: i, ...Object.fromEntries(r.map((v, j) => [`c${j}`, v])) }))}
              columns={cols.map((c, j) => ({ title: c, dataIndex: `c${j}`, ellipsis: true }))}
            />
          )}
          {res.note && <Paragraph type="secondary" style={{ marginTop: 8 }}>{res.note}</Paragraph>}
        </Card>
      )}
    </div>
  );
}

// Hisab-kitab: AI ka step-by-step jod-ghata, aur server khud total dobara jodta hai.
function AiCalc() {
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const voice = useVoiceInput((t) => setQ(p => (p ? p + ' ' : '') + t));

  const run = async () => {
    if (!q.trim()) return antMessage.warning('Hisab likho');
    setLoading(true); setRes(null);
    try { setRes((await api.post('/ai/calc', { question: q.trim() })).data); }
    catch (err) { antMessage.error(err.response?.data?.error || 'Hisab nahi ho paaya'); }
    setLoading(false);
  };

  return (
    <div>
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="Jod-ghata, percentage, share, GST — sab hisab AI kar dega, poora kaam dikha ke"
        description='Jaise: "5000 studio, 2500 poster, 12000 shoot — total kitna aur 30% partner share kitna", "45000 me se 12 din absent ka kata hua kitna".'
      />
      <TextArea rows={3} value={q} onChange={e => setQ(e.target.value)} placeholder="Hisab likho ya bol do" style={{ marginBottom: 10 }} />
      <Space wrap style={{ marginBottom: 12 }}>
        {voice.supported && (
          <Button icon={<AudioOutlined />} danger={voice.listening} onClick={voice.toggle}>
            {voice.listening ? 'Sun raha hoon...' : 'Bol ke batao'}
          </Button>
        )}
        <Button type="primary" icon={<ThunderboltOutlined />} loading={loading} onClick={run}>Hisab karo</Button>
      </Space>

      {loading && <div><Spin /> <Text type="secondary">Hisab kar raha hoon...</Text></div>}

      {res && (
        <Card size="small">
          {res.answer && <Paragraph style={{ fontSize: 16 }}><Text strong>{res.answer}</Text></Paragraph>}
          {res.items?.length > 0 && (
            <Table
              size="small" pagination={false} style={{ marginBottom: 10 }}
              dataSource={res.items.map((r, i) => ({ ...r, key: i }))}
              columns={[
                { title: 'Kya', dataIndex: 'kya' },
                { title: 'Amount', dataIndex: 'amount', align: 'right', render: v => inr(v) },
              ]}
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}><Text strong>Total</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right"><Text strong>{inr(res.checked_total)}</Text></Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          )}
          {res.mismatch && (
            <Alert
              type="warning" showIcon style={{ marginBottom: 10 }}
              message={`AI ne total ${inr(res.ai_total)} likha, par items jod ke ${inr(res.checked_total)} aata hai — sahi total ${inr(res.checked_total)} maano.`}
            />
          )}
          {res.steps?.length > 0 && (
            <>
              <Text strong>Hisab:</Text>
              <ul style={{ marginTop: 4 }}>{res.steps.map((s, k) => <li key={k}>{s}</li>)}</ul>
            </>
          )}
          {res.note && <Paragraph type="secondary">{res.note}</Paragraph>}
        </Card>
      )}
    </div>
  );
}

const THUMB_CATEGORIES = [
  { value: 'music', label: 'Music' },
  { value: 'distribution', label: 'Distribution' },
  { value: 'new_song_distribution', label: 'New Song Distribution' },
  { value: 'bhajan', label: 'Bhajan' },
  { value: 'channel_logo', label: 'Channel Logo' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'channel_art', label: 'Channel Art' },
];

// Thumbnail / poster AI se banwana. Gemini ka image model paid hai — key par billing
// na ho to saaf message aata hai, baaki AI free me chalta rehta hai.
function ImageMaker() {
  const [prompt, setPrompt] = useState('');
  const [text, setText] = useState('');
  const [style, setStyle] = useState('bright cinematic YouTube thumbnail');
  const [ratio, setRatio] = useState('16:9');
  const [category, setCategory] = useState('music');
  const [ref, setRef] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingIdx, setSavingIdx] = useState(-1);
  const [billing, setBilling] = useState(false);
  const fileRef = useRef(null);

  const gen = async (save = false) => {
    if (!prompt.trim()) return antMessage.warning('Kaisi image chahiye wo likho');
    if (save) setSavingIdx(0); else { setLoading(true); setImages([]); setBilling(false); }
    try {
      let r;
      if (ref) {
        const fd = new FormData();
        fd.append('file', ref);
        fd.append('prompt', prompt.trim());
        fd.append('style', style);
        fd.append('text', text);
        r = await api.post('/ai/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        r = await api.post('/ai/image', {
          prompt: prompt.trim(), style, ratio, text,
          save, name: text || prompt.trim(), category,
        });
      }
      setImages(r.data?.images || []);
      if (save && r.data?.saved) antMessage.success('Thumbnail Library me save ho gaya');
    } catch (err) {
      const d = err.response?.data;
      if (d?.needs_billing) setBilling(true);
      antMessage.error(d?.error || 'Image nahi bani');
    }
    setLoading(false); setSavingIdx(-1);
  };

  const dl = (img, i) => downloadB64({
    name: (text || 'thumbnail').replace(/[^a-zA-Z0-9\- ]/g, '') + `-${i + 1}.png`,
    mime: img.mime, data: img.data,
  });

  return (
    <div>
      <Alert
        type="warning" showIcon style={{ marginBottom: 12 }}
        message="Image banane wala Gemini model paid hai"
        description="Baaki saara AI free chalta hai, par image banane ke liye Google AI Studio me is key wale project par billing on karni padegi (aistudio.google.com/apikey). Ek image ~₹3."
      />
      <Row gutter={[10, 10]}>
        <Col xs={24} md={16}>
          <TextArea rows={3} value={prompt} onChange={e => setPrompt(e.target.value)}
            placeholder='Kya banana hai? jaise: "Radha-Krishna Vrindavan me, bhakti thumbnail, diye aur phool"' />
          <VoiceBtn block onText={appendText(setPrompt)} label="Bol ke batao kya image banani hai" />
        </Col>
        <Col xs={24} md={8}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Input value={text} onChange={e => setText(e.target.value)} placeholder="Image par likhna hai (jaise: RADHE RADHE)" />
            <Input value={style} onChange={e => setStyle(e.target.value)} placeholder="Style" />
            <Select value={ratio} onChange={setRatio} style={{ width: '100%' }} options={[
              { value: '16:9', label: 'YouTube thumbnail 16:9' },
              { value: '1:1', label: 'Square 1:1' },
              { value: '9:16', label: 'Shorts / Story 9:16' },
              { value: '4:3', label: '4:3' },
            ]} />
          </Space>
        </Col>
      </Row>

      <Space wrap style={{ margin: '12px 0' }}>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; if (f) setRef(f); e.target.value = ''; }} />
        <Button icon={<UploadOutlined />} onClick={() => fileRef.current?.click()}>
          {ref ? `Photo: ${ref.name}` : 'Apni photo se banwao (optional)'}
        </Button>
        {ref && <Button size="small" onClick={() => setRef(null)}>Photo hatao</Button>}
        <Select value={category} onChange={setCategory} style={{ width: 200 }} options={THUMB_CATEGORIES} />
        <Button type="primary" icon={<PictureOutlined />} loading={loading} onClick={() => gen(false)}>Image banao</Button>
        <Button icon={<CheckCircleOutlined />} loading={savingIdx === 0} disabled={!!ref}
          onClick={() => gen(true)}>Banao + Library me save</Button>
      </Space>

      {billing && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }}
          message="Quota nahi hai"
          description={<span>Google AI Studio me billing enable karo: <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer">aistudio.google.com/apikey</a>. Uske baad ye page turant kaam karega.</span>} />
      )}
      {loading && <div><Spin /> <Text type="secondary">AI image bana raha hai (30-60 second)...</Text></div>}

      <Row gutter={[12, 12]}>
        {images.map((img, i) => (
          <Col xs={24} sm={12} key={i}>
            <Card size="small" cover={<img alt={`ai-${i}`} src={`data:${img.mime};base64,${img.data}`} style={{ width: '100%' }} />}>
              <Button size="small" type="primary" onClick={() => dl(img, i)}>Download</Button>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
}

// AI Center — every AI feature of the dashboard in one place. Admin gets everything;
// staff only see what the admin has granted (ai_assistant / ai_tools permissions).
export default function AiCenter({ user }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get('/ai/status').then(res => setStatus(res.data)).catch(() => setStatus({ configured: false }));
  }, []);

  // The server is the authority on what this user's AI may see/do.
  const isAdmin = status ? !!status.admin : ['admin', 'super_admin', 'manager', 'hr_manager'].includes(user?.role);
  const isFullAdmin = status ? !!status.full_admin : false;
  const canAssistant = status ? !!status.assistant : false;
  const canTools = status ? !!status.tools : false;

  const items = [];
  // Suggestions come from the server so staff only see questions for sections they can open.
  const suggestions = status?.suggestions?.length ? status.suggestions : (isAdmin ? SUGGESTED : STAFF_SUGGESTED);
  if (canAssistant) items.push({ key: 'ask', label: <span><RobotOutlined /> AI Assistant</span>, children: <Assistant suggestions={suggestions} /> });
  if (canTools) items.push({ key: 'seo', label: <span><YoutubeOutlined /> SEO Writer</span>, children: <SeoWriter /> });
  if (canTools) items.push({ key: 'email', label: <span><MailOutlined /> Email Writer</span>, children: <EmailWriter isAdmin={isFullAdmin} /> });
  if (isFullAdmin) items.push({ key: 'actions', label: <span><ThunderboltOutlined /> AI se Kaam</span>, children: <AiActions /> });
  if (canTools) items.push({ key: 'make', label: <span><FileExcelOutlined /> Excel / PDF Banao</span>, children: <AiMaker isAdmin={isAdmin} /> });
  if (canAssistant) items.push({ key: 'calc', label: <span><ThunderboltOutlined /> Hisab (Calculator)</span>, children: <AiCalc /> });
  if (isAdmin) items.push({ key: 'imgmake', label: <span><PictureOutlined /> Image / Thumbnail Banao</span>, children: <ImageMaker /> });
  if (isAdmin) items.push({ key: 'excel', label: <span><FileExcelOutlined /> Excel Reader</span>, children: <ExcelReader /> });
  if (canTools) items.push({ key: 'thumb', label: <span><PictureOutlined /> Thumbnail Check</span>, children: <ThumbCheck /> });
  if (isAdmin) items.push({ key: 'songcheck', label: <span><AuditOutlined /> Song Check</span>, children: <SongCheck /> });
  if (isAdmin) items.push({ key: 'pnl', label: <span><RiseOutlined /> Profit & Loss Salah</span>, children: <PnlAdvice /> });
  if (isAdmin) items.push({ key: 'followup', label: <span><AlertOutlined /> Client Follow-up</span>, children: <ClientFollowup /> });
  if (isAdmin) items.push({ key: 'clientsum', label: <span><ProfileOutlined /> Client Statement</span>, children: <ClientSummary /> });
  if (isAdmin) items.push({ key: 'claimai', label: <span><SafetyOutlined /> Claim Assistant</span>, children: <ClaimAssistant /> });
  if (isAdmin) items.push({ key: 'chalert', label: <span><FallOutlined /> Channel Alert</span>, children: <ChannelAlert /> });
  if (isAdmin) items.push({ key: 'uploadplan', label: <span><CalendarOutlined /> Upload Plan</span>, children: <UploadPlan /> });
  if (isAdmin) items.push({ key: 'insights', label: <span><BulbOutlined /> Business Insights</span>, children: <Insights /> });
  if (isAdmin) items.push({ key: 'staff', label: <span><TeamOutlined /> Staff Review</span>, children: <StaffReview /> });
  if (isAdmin) items.push({ key: 'daily', label: <span><FileTextOutlined /> Daily AI Report</span>, children: <DailyReport /> });
  items.push({ key: 'guide', label: <span><QuestionCircleOutlined /> Dashboard Guide</span>, children: <Guide /> });
  if (isAdmin) items.push({ key: 'log', label: <span><HistoryOutlined /> AI Log</span>, children: <AiLog /> });

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          <RobotOutlined style={{ color: '#4f46e5' }} /> AI Center
        </Title>
        <Paragraph type="secondary" style={{ margin: 0 }}>
          Apne dashboard ka AI — kuch bhi poochho, SEO/email likhwao, business review aur roz ka report lo.
        </Paragraph>
      </div>

      {status && !status.configured && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 16 }}
          message="AI key set nahi hai"
          description="Server par Gemini API key lagni baaki hai — usse pehle AI jawab nahi dega."
        />
      )}

      <Card bodyStyle={{ paddingTop: 8 }}>
        {status ? <Tabs items={items} /> : <Spin />}
      </Card>
    </div>
  );
}
